import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './P2pMerchantDashboard.css';

// Mock data for merchant dashboard
const mockMerchantStats = {
  totalTrades30d: 127,
  volumeTraded30d: 15842.50,
  net: 1494.32,
  fees: 18.75,
  netAfterFees: 1475.57,
  displayName: 'CryptoKing',
  status: 'Verified Merchant',
  merchantStatus: 'Fast Trader',
  approvedSince: '15-03-2024',
  activeOffers: 5,
  pausedOffers: 2,
  activeBuyOffers: 3,
  activeSellOffers: 2,
  totalTrades: 1247,
  completedTrades: 1198,
  completionRate: 96.07,
  totalVolume: 125840.00,
  ongoingTrades: 3,
  disputedTrades: 1,
  currentDeposit: 500.00,
  requiredDeposit: 100.00,
  requirementMet: true,
  merchantLevel: 'Gold',
  successRate30d: 98.2,
  avgResponseTime: '2 min',
  rating: 4.9,
  totalReviews: 856
};

const mockRecentTrades = [
  {
    id: '9520579...ced686',
    type: 'sell',
    amount: 25000.00,
    currency: 'NGN',
    usdtAmount: 16.50,
    status: 'Completed',
    date: '2 hours ago',
    buyer: '@trader_mike'
  },
  {
    id: '5d6431d...5b0f9d',
    type: 'buy',
    amount: 15000.00,
    currency: 'NGN',
    usdtAmount: 10.00,
    status: 'In Progress',
    date: '5 hours ago',
    buyer: '@crypto_sarah'
  },
  {
    id: '2bf1a76...46ac23',
    type: 'sell',
    amount: 45000.00,
    currency: 'NGN',
    usdtAmount: 30.00,
    status: 'Completed',
    date: 'Yesterday',
    buyer: '@john_doe'
  }
];

const P2pMerchantDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'trades' | 'earnings'>('overview');
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [editedStatus, setEditedStatus] = useState(mockMerchantStats.merchantStatus);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Gold': return '#f59e0b';
      case 'Platinum': return '#94a3b8';
      case 'Diamond': return '#60a5fa';
      default: return '#22c55e';
    }
  };

  const getLevelGradient = (level: string) => {
    switch (level) {
      case 'Gold': return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      case 'Platinum': return 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)';
      case 'Diamond': return 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)';
      default: return 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)';
    }
  };

  const handleSaveStatus = () => {
    // In a real app, this would save to backend
    mockMerchantStats.merchantStatus = editedStatus;
    setIsStatusModalOpen(false);
  };

  const handleCancelStatus = () => {
    setEditedStatus(mockMerchantStats.merchantStatus);
    setIsStatusModalOpen(false);
  };

  return (
    <div className="merchant-container">
      {/* Header */}
      <header className="merchant-header">
        <button className="merchant-back-button" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="merchant-title">Merchant Dashboard</h1>
        <button className="merchant-settings-button" aria-label="Settings">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="10" cy="10" r="2" stroke="currentColor" strokeWidth="2"/>
            <path d="M10 2V4M10 16V18M18 10H16M4 10H2M15.66 4.34L14.24 5.76M5.76 14.24L4.34 15.66M15.66 15.66L14.24 14.24M5.76 5.76L4.34 4.34" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </header>

      {/* Hero Section - Merchant Profile */}
      <div className="merchant-hero">
        <div className="merchant-hero-bg"></div>
        <div className="merchant-hero-content">
          <div className="merchant-avatar-wrapper">
            <div className="merchant-avatar">
              <span>CK</span>
            </div>
            <div className="merchant-level-badge" style={{ background: getLevelGradient(mockMerchantStats.merchantLevel) }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M6 1L7.5 4.5L11 5L8.5 7.5L9 11L6 9.5L3 11L3.5 7.5L1 5L4.5 4.5L6 1Z" fill="currentColor"/>
              </svg>
              <span>{mockMerchantStats.merchantLevel}</span>
            </div>
          </div>
          <div className="merchant-profile-info">
            <div className="merchant-name-row">
              <h2 className="merchant-display-name">{mockMerchantStats.displayName}</h2>
              <button
                className="merchant-edit-status-btn"
                onClick={() => setIsStatusModalOpen(true)}
                aria-label="Edit merchant status"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M10.5 1.5L12.5 3.5L4.5 11.5L1.5 12.5L2.5 9.5L10.5 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8.5 3.5L10.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Edit</span>
              </button>
            </div>
            <div className="merchant-verified-badge">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6" fill="#22c55e"/>
                <path d="M4.5 7L6 8.5L9.5 5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{mockMerchantStats.status}</span>
            </div>
          </div>
          <div className="merchant-rating-row">
            <div className="merchant-rating">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1L10 5.5L15 6L11.5 9.5L12.5 14.5L8 12L3.5 14.5L4.5 9.5L1 6L6 5.5L8 1Z" fill="#f59e0b"/>
              </svg>
              <span className="rating-value">{mockMerchantStats.rating}</span>
              <span className="rating-count">({mockMerchantStats.totalReviews} reviews)</span>
            </div>
            <span className="merchant-joined">Since {mockMerchantStats.approvedSince}</span>
          </div>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="merchant-quick-stats">
        <div className="quick-stat-item">
          <div className="quick-stat-value">{mockMerchantStats.totalTrades}</div>
          <div className="quick-stat-label">Total Trades</div>
        </div>
        <div className="quick-stat-divider"></div>
        <div className="quick-stat-item">
          <div className="quick-stat-value">{mockMerchantStats.successRate30d}%</div>
          <div className="quick-stat-label">Success Rate</div>
        </div>
        <div className="quick-stat-divider"></div>
        <div className="quick-stat-item">
          <div className="quick-stat-value">{mockMerchantStats.avgResponseTime}</div>
          <div className="quick-stat-label">Avg Response</div>
        </div>
      </div>

      {/* Stats Cards with Gradients */}
      <div className="merchant-stats-grid">
        <div className="merchant-stat-card merchant-stat-card-green">
          <div className="stat-card-glow"></div>
          <div className="merchant-stat-icon-wrapper">
            <div className="merchant-stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2V22M12 2L6 8M12 2L18 8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="merchant-stat-value">{mockMerchantStats.totalTrades30d}</div>
          <div className="merchant-stat-label">Trades (30d)</div>
          <div className="stat-trend positive">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 9V3M6 3L3 6M6 3L9 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>+12%</span>
          </div>
        </div>

        <div className="merchant-stat-card merchant-stat-card-orange">
          <div className="stat-card-glow"></div>
          <div className="merchant-stat-icon-wrapper">
            <div className="merchant-stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="merchant-stat-value">${mockMerchantStats.volumeTraded30d.toLocaleString()}</div>
          <div className="merchant-stat-label">Volume (30d)</div>
          <div className="stat-trend positive">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M6 9V3M6 3L3 6M6 3L9 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>+8.5%</span>
          </div>
        </div>
      </div>

      {/* Earnings Card */}
      <div className="merchant-earnings-card">
        <div className="earnings-header">
          <div className="earnings-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 6V18M8 10L12 6L16 10M8 14L12 18L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="earnings-info">
            <span className="earnings-label">Net Earnings (30d)</span>
            <span className="earnings-value">${mockMerchantStats.netAfterFees.toLocaleString()}</span>
          </div>
        </div>
        <div className="earnings-breakdown">
          <div className="earnings-item">
            <span className="earnings-item-label">Gross</span>
            <span className="earnings-item-value">${mockMerchantStats.net.toLocaleString()}</span>
          </div>
          <div className="earnings-item">
            <span className="earnings-item-label">Fees</span>
            <span className="earnings-item-value negative">-${mockMerchantStats.fees}</span>
          </div>
        </div>
        <div className="earnings-progress">
          <div className="earnings-progress-bar" style={{ width: '75%' }}></div>
        </div>
        <div className="earnings-goal">75% to monthly goal ($2,000)</div>
      </div>

      {/* Performance Section */}
      <div className="merchant-section merchant-performance-section">
        <div className="merchant-section-header">
          <h2 className="merchant-section-title">Performance</h2>
          <div className="merchant-period-badge">Last 30 days</div>
        </div>

        <div className="performance-grid">
          <div className="performance-item">
            <div className="performance-circle">
              <svg viewBox="0 0 36 36" className="circular-chart">
                <path className="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
                <path className="circle-progress green" strokeDasharray={`${mockMerchantStats.completionRate}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
              </svg>
              <div className="performance-percentage">{mockMerchantStats.completionRate}%</div>
            </div>
            <span className="performance-label">Completion</span>
          </div>

          <div className="performance-item">
            <div className="performance-circle">
              <svg viewBox="0 0 36 36" className="circular-chart">
                <path className="circle-bg" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
                <path className="circle-progress orange" strokeDasharray={`${mockMerchantStats.successRate30d}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"/>
              </svg>
              <div className="performance-percentage">{mockMerchantStats.successRate30d}%</div>
            </div>
            <span className="performance-label">Success Rate</span>
          </div>

          <div className="performance-item">
            <div className="performance-value-box">
              <span className="performance-big-value">{mockMerchantStats.ongoingTrades}</span>
            </div>
            <span className="performance-label">Ongoing</span>
          </div>

          <div className="performance-item">
            <div className="performance-value-box disputed">
              <span className="performance-big-value">{mockMerchantStats.disputedTrades}</span>
            </div>
            <span className="performance-label">Disputed</span>
          </div>
        </div>
      </div>

      {/* Offers Section */}
      <div className="merchant-section">
        <div className="merchant-section-header">
          <h2 className="merchant-section-title">My Offers</h2>
          <button className="merchant-add-button">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span>Add</span>
          </button>
        </div>

        <div className="offers-grid">
          <div className="offer-card offer-card-buy">
            <div className="offer-card-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 15V5M10 5L5 10M10 5L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="offer-card-info">
              <span className="offer-card-count">{mockMerchantStats.activeBuyOffers}</span>
              <span className="offer-card-label">Buy Offers</span>
            </div>
          </div>

          <div className="offer-card offer-card-sell">
            <div className="offer-card-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 5V15M10 15L15 10M10 15L5 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="offer-card-info">
              <span className="offer-card-count">{mockMerchantStats.activeSellOffers}</span>
              <span className="offer-card-label">Sell Offers</span>
            </div>
          </div>

          <div className="offer-card offer-card-paused">
            <div className="offer-card-icon">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect x="5" y="4" width="3" height="12" rx="1" fill="currentColor"/>
                <rect x="12" y="4" width="3" height="12" rx="1" fill="currentColor"/>
              </svg>
            </div>
            <div className="offer-card-info">
              <span className="offer-card-count">{mockMerchantStats.pausedOffers}</span>
              <span className="offer-card-label">Paused</span>
            </div>
          </div>
        </div>
      </div>

      {/* Security Deposit */}
      <div className="merchant-section merchant-deposit-section">
        <div className="merchant-section-header">
          <div className="deposit-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L21 7V12C21 17 12 22 12 22C12 22 3 17 3 12V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
              <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="merchant-section-title">Security Deposit</h2>
        </div>

        <div className="deposit-amount-row">
          <div className="deposit-current">
            <span className="deposit-value">${mockMerchantStats.currentDeposit.toFixed(2)}</span>
            <span className="deposit-label">Current</span>
          </div>
          <div className="deposit-divider">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="deposit-required">
            <span className="deposit-value">${mockMerchantStats.requiredDeposit.toFixed(2)}</span>
            <span className="deposit-label">Required</span>
          </div>
        </div>

        <div className="deposit-status">
          <div className="deposit-status-badge success">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="6" fill="currentColor" fillOpacity="0.2"/>
              <path d="M4.5 7L6 8.5L9.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Requirement Met</span>
          </div>
        </div>

        <button className="deposit-manage-button">
          <span>Manage Deposit</span>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {/* Recent Trades */}
      <div className="merchant-section">
        <div className="merchant-section-header">
          <h2 className="merchant-section-title">Recent Trades</h2>
          <button className="merchant-view-all">View All</button>
        </div>

        <div className="merchant-trades-list">
          {mockRecentTrades.map((trade, index) => (
            <div key={index} className={`merchant-trade-card ${trade.type}`}>
              <div className="trade-type-indicator"></div>
              <div className="merchant-trade-content">
                <div className="merchant-trade-header">
                  <div className={`trade-type-badge ${trade.type}`}>
                    {trade.type === 'buy' ? (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M6 9V3M6 3L3 6M6 3L9 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M6 3V9M6 9L9 6M6 9L3 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    <span>{trade.type === 'buy' ? 'Buy' : 'Sell'}</span>
                  </div>
                  <div className={`merchant-trade-status ${trade.status === 'Completed' ? 'completed' : 'progress'}`}>
                    {trade.status === 'Completed' ? (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M3 6L5 8L9 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2"/>
                      </svg>
                    )}
                    <span>{trade.status}</span>
                  </div>
                </div>
                <div className="merchant-trade-amounts">
                  <span className="trade-fiat-amount">{trade.amount.toLocaleString()} {trade.currency}</span>
                  <span className="trade-crypto-amount">{trade.usdtAmount} USDT</span>
                </div>
                <div className="merchant-trade-footer">
                  <span className="trade-partner">{trade.buyer}</span>
                  <span className="trade-time">{trade.date}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="merchant-quick-actions">
        <button className="quick-action-btn">
          <div className="quick-action-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 15V5M10 5L5 10M10 5L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span>Create Buy Ad</span>
        </button>
        <button className="quick-action-btn">
          <div className="quick-action-icon">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 5V15M10 15L15 10M10 15L5 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <span>Create Sell Ad</span>
        </button>
      </div>

      {/* Edit Status Modal */}
      {isStatusModalOpen && (
        <>
          <div className="merchant-modal-overlay" onClick={handleCancelStatus}></div>
          <div className="merchant-status-modal">
            <div className="merchant-status-modal-header">
              <div className="merchant-status-modal-title-row">
                <div className="merchant-status-modal-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2L13 8L19 9L14.5 13.5L15.5 19.5L10 16.5L4.5 19.5L5.5 13.5L1 9L7 8L10 2Z" fill="currentColor"/>
                  </svg>
                </div>
                <h3 className="merchant-status-modal-title">Merchant Status</h3>
              </div>
              <button
                className="merchant-status-modal-close"
                onClick={handleCancelStatus}
                aria-label="Close modal"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            
            <div className="merchant-status-modal-body">
              <input
                type="text"
                className="merchant-status-input"
                value={editedStatus}
                onChange={(e) => setEditedStatus(e.target.value)}
                placeholder="Enter merchant status"
              />
            </div>

            <div className="merchant-status-modal-actions">
              <button
                className="merchant-status-btn merchant-status-btn-cancel"
                onClick={handleCancelStatus}
              >
                Cancel
              </button>
              <button
                className="merchant-status-btn merchant-status-btn-save"
                onClick={handleSaveStatus}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default P2pMerchantDashboard;